import { HttpClient } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { environment } from '@et/environment';
import { isOnline } from '@et/utils';

@Component({
  selector: 'auth-forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.scss'],
  standalone: false,
})
export class ForgotPasswordComponent {
  private fb = inject(UntypedFormBuilder);
  private http = inject(HttpClient);

  form: UntypedFormGroup = this.fb.group({
    email: [null, [Validators.required, Validators.email]],
  });

  submitted = false;
  /** Shown after submit regardless of outcome — Better Auth's endpoint itself
   * always returns 200 whether or not the email is registered (avoids
   * account-enumeration), so there is no success/failure branch to key off. */
  requestSent = false;

  async requestReset(email: string) {
    if (!isOnline()) {
      this.form.get('email')?.setErrors({ isOffline: true });
      return;
    }
    this.submitted = true;
    try {
      // Use the product web app origin — never `window.location.origin`.
      // Native (Capacitor/Electron) origins are custom schemes
      // (`capacitor://localhost`, Electron protocol) that email clients cannot
      // open; admin already uses `webAppOrigin` for the same reason.
      await firstValueFrom(
        this.http.post(
          `${environment.betterAuth.baseUrl}/api/auth/request-password-reset`,
          {
            email,
            redirectTo: `${environment.betterAuth.webAppOrigin}/auth/reset-password`,
          },
          { withCredentials: true },
        ),
      );
    } catch {
      // Only a transport-level failure (network/CORS/5xx) reaches here — same
      // generic message either way, so nothing distinguishes "unknown email"
      // from "delivery failed".
    } finally {
      this.submitted = false;
      this.requestSent = true;
    }
  }
}
